// ==========================================
// SISTEMA UNIFICADO DE NÍVEIS + SELOS - BORAFRETE
// ==========================================
import { useState, useEffect, useMemo } from 'react';

export const NIVEIS_MOTORISTA = [
  { 
    id: 'bronze', 
    nome: 'Bronze', 
    entregas: 0, 
    cor: 'from-amber-600 to-amber-700',
    bg: 'bg-amber-600',
    text: 'text-amber-700',
    icon: 'Award',
    descricao: 'Início da jornada',
    beneficios: ['Acesso às cargas disponíveis', 'Suporte padrão'],
    sorteio: []
  },
  { 
    id: 'prata', 
    nome: 'Prata', 
    entregas: 10, 
    cor: 'from-slate-400 to-slate-500',
    bg: 'bg-slate-400',
    text: 'text-slate-700',
    icon: 'Award',
    descricao: 'Motorista dedicado',
    beneficios: ['Prioridade em cargas', 'Suporte prioritário'],
    sorteio: []
  },
  { 
    id: 'ouro', 
    nome: 'Ouro', 
    entregas: 25, 
    cor: 'from-yellow-400 to-yellow-600',
    bg: 'bg-yellow-500',
    text: 'text-yellow-700',
    icon: 'Trophy',
    descricao: '🎉 Participação em sorteios!',
    beneficios: ['Participa do sorteio mensal', 'Cargas exclusivas', 'Desconto em combustível'],
    sorteio: ['📱 Celular', '💻 Notebook', '📺 TV']
  },
  { 
    id: 'diamante', 
    nome: 'Diamante', 
    entregas: 50, 
    cor: 'from-cyan-400 to-blue-600',
    bg: 'bg-cyan-500',
    text: 'text-cyan-700',
    icon: 'Trophy',
    descricao: '🚗 Sorteio: Carro Popular!',
    beneficios: ['Sorteio especial', 'Cargas premium', 'Antecipação de pagamento'],
    sorteio: ['🚗 Carro Popular 0km']
  },
  { 
    id: 'black', 
    nome: 'Black Diamante', 
    entregas: 100, 
    cor: 'from-slate-800 to-black',
    bg: 'bg-slate-900',
    text: 'text-white',
    icon: 'Star',
    descricao: '🚛 Sorteio: Caminhão Cavalinho!',
    beneficios: ['Cargas VIP', 'Pagamento imediato', 'Suporte 24h dedicado'],
    sorteio: ['🚛 Caminhão Cavalo Mecânico']
  }
];

// ==========================================
// SELOS DE CONQUISTA - SEM DISTÂNCIA/KM
// ==========================================
export const SELOS_MOTORISTA = [
  {
    id: 'primeira_entrega',
    nome: 'Primeira Entrega',
    descricao: 'Complete sua primeira entrega',
    icon: 'Truck',
    pontos: 10,
    cor: 'bg-green-500',
    condicao: (entregas) => entregas >= 1
  },
  {
    id: 'entregador_10',
    nome: 'Entregador 10',
    descricao: 'Complete 10 entregas',
    icon: 'Package',
    pontos: 25,
    cor: 'bg-blue-500',
    condicao: (entregas) => entregas >= 10
  },
  {
    id: 'entregador_25',
    nome: 'Entregador 25',
    descricao: 'Complete 25 entregas',
    icon: 'Trophy',
    pontos: 50,
    cor: 'bg-amber-500',
    condicao: (entregas) => entregas >= 25
  },
  {
    id: 'entregador_50',
    nome: 'Entregador 50',
    descricao: 'Complete 50 entregas',
    icon: 'Crown',
    pontos: 100,
    cor: 'bg-purple-500',
    condicao: (entregas) => entregas >= 50
  },
  {
    id: 'entregador_100',
    nome: 'Entregador 100',
    descricao: 'Complete 100 entregas',
    icon: 'Star',
    pontos: 250,
    cor: 'bg-slate-900',
    condicao: (entregas) => entregas >= 100
  },
  {
    id: 'velocista',
    nome: 'Velocista',
    descricao: 'Conclua 5 entregas no mesmo mês',
    icon: 'Zap',
    pontos: 30,
    cor: 'bg-orange-500',
    condicao: (entregas, stats) => (stats?.esteMes || 0) >= 5
  },
  {
    id: 'fidelidade',
    nome: 'Fidelidade',
    descricao: 'Complete 10 entregas para a mesma empresa',
    icon: 'Heart',
    pontos: 40,
    cor: 'bg-red-500',
    condicao: (entregas, stats) => (stats?.maxEntregasEmpresa || 0) >= 10
  },
  {
    id: 'profissional',
    nome: 'Profissional',
    descricao: 'Mantenha 100% de entregas no prazo',
    icon: 'Shield',
    pontos: 50,
    cor: 'bg-indigo-500',
    condicao: (entregas, stats) => entregas >= 5 && (stats?.pontualidade || 100) >= 95
  }
];

// ==========================================
// FUNÇÕES UNIFICADAS
// ==========================================

/**
 * Calcula nível baseado nas entregas realizadas
 */
export const calcularNivel = (entregas = 0) => {
  let nivel = NIVEIS_MOTORISTA[0];
  
  for (let i = 0; i < NIVEIS_MOTORISTA.length; i++) {
    if (entregas >= NIVEIS_MOTORISTA[i].entregas) {
      nivel = NIVEIS_MOTORISTA[i];
    }
  }
  
  return nivel;
};

/**
 * Calcula progresso para próximo nível
 */
export const calcularProgresso = (entregas = 0) => {
  const nivelAtual = calcularNivel(entregas);
  const indexAtual = NIVEIS_MOTORISTA.findIndex(n => n.id === nivelAtual.id);
  const proximoNivel = NIVEIS_MOTORISTA[indexAtual + 1];
  
  if (!proximoNivel) return 100;
  
  const entregasNoNivel = entregas - nivelAtual.entregas;
  const entregasNecessarias = proximoNivel.entregas - nivelAtual.entregas;
  
  return Math.min(100, Math.round((entregasNoNivel / entregasNecessarias) * 100));
};

/**
 * Busca entregas realizadas do motorista baseado nos CONTRATOS
 */
export const buscarEntregasRealizadas = (user) => {
  if (!user) return 0;
  
  try {
    const contratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
    
    const meusContratos = contratos.filter(c => {
      const matchEmail = c.motorista_email === user.email;
      const matchId = c.motorista_id === user.id;
      const matchNome = c.motorista_nome === user.nome;
      return matchEmail || matchId || matchNome;
    });
    
    const entregasConcluidas = meusContratos.filter(c => 
      c.status === 'concluido' || c.status === 'entregue'
    ).length;
    
    console.log(`📊 Entregas calculadas para ${user.email}: ${entregasConcluidas}`);
    return entregasConcluidas;
    
  } catch (error) {
    console.error('Erro ao buscar entregas:', error);
    return user.entregasRealizadas || 
           parseInt(localStorage.getItem(`entregas_${user.id}`)) || 
           0;
  }
};

/**
 * Calcula estatísticas do motorista
 */
export const calcularStats = (user) => {
  if (!user) return { valorTotal: 0, esteMes: 0, maxEntregasEmpresa: 0, pontualidade: 100 };
  
  try {
    const contratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
    const meusContratos = contratos.filter(c => {
      return c.motorista_email === user.email || 
             c.motorista_id === user.id || 
             c.motorista_nome === user.nome;
    });
    
    const concluidos = meusContratos.filter(c => 
      c.status === 'concluido' || c.status === 'entregue'
    );
    
    // Valor total
    const valorTotal = concluidos.reduce((sum, c) => sum + (parseFloat(c.valor) || 0), 0);
    
    // Este mês
    const hoje = new Date();
    const esteMes = concluidos.filter(c => {
      const data = c.data_fim ? new Date(c.data_fim) : new Date(c.created_at);
      return data.getMonth() === hoje.getMonth() && data.getFullYear() === hoje.getFullYear();
    }).length;
    
    // Max entregas para mesma empresa
    const empresas = {};
    concluidos.forEach(c => {
      const emp = c.empresa_nome || c.empresa_id || 'desconhecida';
      empresas[emp] = (empresas[emp] || 0) + 1;
    });
    const maxEntregasEmpresa = Math.max(0, ...Object.values(empresas));
    
    // Pontualidade (simplificada - assume 100% se não houver atrasos registrados)
    const pontualidade = 100;
    
    return { valorTotal, esteMes, maxEntregasEmpresa, pontualidade };
  } catch (error) {
    return { valorTotal: 0, esteMes: 0, maxEntregasEmpresa: 0, pontualidade: 100 };
  }
};

/**
 * Calcula selos conquistados
 */
export const calcularSelos = (entregas, stats) => {
  return SELOS_MOTORISTA.map(selo => ({
    ...selo,
    conquistado: selo.condicao(entregas, stats)
  }));
};

/**
 * Calcula pontuação total
 */
export const calcularPontuacao = (selos) => {
  if (!Array.isArray(selos)) return 0;
  return selos
    .filter(s => s.conquistado)
    .reduce((sum, s) => sum + (s.pontos || 0), 0);
};

/**
 * Hook completo para usar em componentes
 */
export const useNivelMotorista = (user) => {
  const [entregas, setEntregas] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const atualizar = () => {
      const total = buscarEntregasRealizadas(user);
      setEntregas(total);
      setLoading(false);
    };

    atualizar();

    const handleStorage = (e) => {
      if (e.key === 'borafrete_contratos') {
        atualizar();
      }
    };

    window.addEventListener('storage', handleStorage);
    const interval = setInterval(atualizar, 3000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [user]);

  // Calcula valores derivados
  const nivel = useMemo(() => calcularNivel(entregas), [entregas]);
  const progresso = useMemo(() => calcularProgresso(entregas), [entregas]);
  
  const proximoNivel = useMemo(() => {
    return NIVEIS_MOTORISTA.find(n => n.entregas > entregas) || null;
  }, [entregas]);

  const entregasRestantes = useMemo(() => {
    if (!proximoNivel) return 0;
    return Math.max(proximoNivel.entregas - entregas, 0);
  }, [entregas, proximoNivel]);

  // Stats, Selos e Pontuação
  const stats = useMemo(() => calcularStats(user), [user, entregas]);
  const selos = useMemo(() => calcularSelos(entregas, stats), [entregas, stats]);
  const pontuacaoTotal = useMemo(() => calcularPontuacao(selos), [selos]);

  const verificarEvolucao = (entregasAnteriores) => {
    const nivelAnterior = calcularNivel(entregasAnteriores);
    return nivel.id !== nivelAnterior.id;
  };

  return {
    entregas,
    nivel,
    progresso,
    proximoNivel,
    entregasRestantes,
    loading,
    verificarEvolucao,
    selos,
    stats,
    pontuacaoTotal,
    NIVEIS: NIVEIS_MOTORISTA
  };
};

/**
 * Verifica se motorista pode participar do sorteio
 */
export const podeParticiparSorteio = (nivelId) => {
  return ['ouro', 'diamante', 'black'].includes(nivelId);
};

/**
 * Formata benefícios para exibição
 */
export const getBeneficiosFormatados = (nivel) => {
  if (!nivel || !nivel.beneficios) return '';
  return nivel.beneficios.join(' • ');
};

export default useNivelMotorista;